import type { DeviceVars } from "../fastboot/client";
import { US_CARRIER_MARKERS, type SupportedModel } from "./supported";

export type PreflightCode =
  | "ok"
  | "needs_bootloader_mode" // is-userspace:yes → reboot-bootloader
  | "unsupported_model"
  | "model_inactive"
  | "carrier_locked" // get_unlock_ability = 0 and locked
  | "us_carrier_variant" // markers found (explains carrier_locked, or warns)
  | "anti_rollback"; // device rollback index > bundle's

export interface PreflightResult {
  ok: boolean;
  code: PreflightCode;
  model: SupportedModel | undefined;
  /** Already unlocked → the unlock step is skipped. */
  alreadyUnlocked: boolean;
  /** get_unlock_ability unsupported by this bootloader; unlock may still fail on-device. */
  unlockAbilityUnknown: boolean;
  carrierHint: string | undefined;
  warnings: PreflightCode[];
}

/**
 * Pure decision function over the vars read in pre-flight. Order of refusal:
 * bootloader mode → model support → active → carrier lock → anti-rollback.
 */
export function evaluatePreflight(
  vars: DeviceVars,
  models: readonly SupportedModel[],
  extraVars: Record<string, string> = {},
  bundleAntiRollback?: number,
): PreflightResult {
  const alreadyUnlocked = vars.unlocked === "yes";
  const unlockAbilityUnknown = vars.unlockAbility === undefined;
  const base: PreflightResult = {
    ok: false,
    code: "ok",
    model: undefined,
    alreadyUnlocked,
    unlockAbilityUnknown,
    carrierHint: undefined,
    warnings: [],
  };

  const haystack = [vars.product, extraVars["variant"], extraVars["sku"], extraVars["carrier"], extraVars["carrier-id"]]
    .filter((s): s is string => typeof s === "string")
    .join(" ");
  const marker = US_CARRIER_MARKERS.find((m) => m.re.test(haystack));
  if (marker) base.carrierHint = marker.carrier;

  if (vars.isUserspace === "yes") return { ...base, code: "needs_bootloader_mode" };

  const product = (vars.product ?? "").toLowerCase();
  const model = models.find((m) => m.codename === product || m.id === product);
  if (!model) return { ...base, code: "unsupported_model" };
  base.model = model;
  if (!model.active) return { ...base, code: "model_inactive" };

  if (!alreadyUnlocked && vars.unlockAbility === false) {
    return { ...base, code: marker ? "us_carrier_variant" : "carrier_locked" };
  }
  if (marker && !alreadyUnlocked) base.warnings.push("us_carrier_variant");

  if (bundleAntiRollback !== undefined && vars.antiRollback !== undefined) {
    const dev = parseInt(vars.antiRollback, 10);
    if (Number.isFinite(dev) && dev > bundleAntiRollback) return { ...base, code: "anti_rollback" };
  }
  return { ...base, ok: true, code: "ok" };
}
